/* eslint-disable max-lines */
import {
  NAVIOBUILD_OFFLINE_CAPABILITIES,
  getNavioBuildOfflineCapability,
} from './offline-capabilities';

export type BuildFeatureLayer = 'setup' | 'operation';
export type BuildTrackerPolicy = 'command_center' | 'creates_tracker_items' | 'visible_in_tracker' | 'not_applicable';
export type BuildHandoverPolicy = 'transfer_package' | 'continuity_context' | 'supporting_evidence' | 'not_applicable';

export interface BuildFeatureContract {
  routeId: string;
  path: string;
  label: string;
  pillar: 'today' | 'project' | 'vessel' | 'money' | 'compliance' | 'delivery';
  domain: string;
  subdomain: string;
  layer: BuildFeatureLayer;
  trackerPolicy: BuildTrackerPolicy;
  handoverPolicy: BuildHandoverPolicy;
  usesSharedLists: boolean;
  requiresSetupReady: boolean;
  offlineRouteId: string;
}

const feature = (contract: BuildFeatureContract): BuildFeatureContract => contract;

export const NAVIOBUILD_FEATURE_CONTRACTS = [
  feature({
    routeId: 'overview',
    path: '/',
    label: 'Overview',
    pillar: 'today',
    domain: 'Today',
    subdomain: 'Command',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'continuity_context',
    usesSharedLists: false,
    requiresSetupReady: false,
    offlineRouteId: 'overview',
  }),
  feature({
    routeId: 'tracker',
    path: '/tracker',
    label: 'Master Tracker',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Command',
    layer: 'operation',
    trackerPolicy: 'command_center',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: false,
    offlineRouteId: 'tracker',
  }),
  feature({
    routeId: 'setup',
    path: '/setup',
    label: 'Setup',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Setup',
    layer: 'setup',
    trackerPolicy: 'not_applicable',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: false,
    offlineRouteId: 'setup',
  }),
  feature({
    routeId: 'lists',
    path: '/settings/lists',
    label: 'Lists',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Setup',
    layer: 'setup',
    trackerPolicy: 'not_applicable',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: false,
    offlineRouteId: 'lists',
  }),
  feature({
    routeId: 'schedule',
    path: '/schedule',
    label: 'Schedule',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Schedule',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'schedule',
  }),
  feature({
    routeId: 'foundation',
    path: '/foundation',
    label: 'Foundation',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Foundation',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'continuity_context',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'foundation',
  }),
  feature({
    routeId: 'records',
    path: '/records',
    label: 'Records & Comms',
    pillar: 'project',
    domain: 'Project',
    subdomain: 'Records & Comms',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'supporting_evidence',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'records',
  }),
  feature({
    routeId: 'spine',
    path: '/spine',
    label: 'Spine',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Technical',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'spine',
  }),
  feature({
    routeId: 'drawings',
    path: '/drawings',
    label: 'Drawings',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Technical',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'drawings',
  }),
  feature({
    routeId: 'tests',
    path: '/tests',
    label: 'Acceptance Tests',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Technical',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'tests',
  }),
  feature({
    routeId: 'quality',
    path: '/quality',
    label: 'NCRs & Snags',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Technical',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'quality',
  }),
  feature({
    routeId: 'osi',
    path: '/osi',
    label: 'OSI',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Technical',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'continuity_context',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'osi',
  }),
  feature({
    routeId: 'visits',
    path: '/visits',
    label: 'Site Visits',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Site Visits',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'supporting_evidence',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'visits',
  }),
  feature({
    routeId: 'brief',
    path: '/brief',
    label: 'Site Visit Brief',
    pillar: 'vessel',
    domain: 'Vessel',
    subdomain: 'Site Visits',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'supporting_evidence',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'brief',
  }),
  feature({
    routeId: 'variations',
    path: '/variations',
    label: 'Variations',
    pillar: 'money',
    domain: 'Commercial',
    subdomain: 'Change Control',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'variations',
  }),
  feature({
    routeId: 'budget',
    path: '/budget',
    label: 'Budget',
    pillar: 'money',
    domain: 'Commercial',
    subdomain: 'Financial Control',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'continuity_context',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'budget',
  }),
  feature({
    routeId: 'payments',
    path: '/payments',
    label: 'Payments',
    pillar: 'money',
    domain: 'Commercial',
    subdomain: 'Payment Gates',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'continuity_context',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'payments',
  }),
  feature({
    routeId: 'warranty',
    path: '/warranty',
    label: 'Warranty',
    pillar: 'money',
    domain: 'Commercial',
    subdomain: 'Warranty',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'continuity_context',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'warranty',
  }),
  feature({
    routeId: 'regulatory',
    path: '/regulatory',
    label: 'Deliverables',
    pillar: 'compliance',
    domain: 'Compliance',
    subdomain: 'Regulatory',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'regulatory',
  }),
  feature({
    routeId: 'ddr',
    path: '/ddr',
    label: 'DDR',
    pillar: 'compliance',
    domain: 'Compliance',
    subdomain: 'Evidence',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'supporting_evidence',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'ddr',
  }),
  feature({
    routeId: 'reports',
    path: '/reports',
    label: 'Reports',
    pillar: 'delivery',
    domain: 'Delivery',
    subdomain: 'Handover & Closeout',
    layer: 'operation',
    trackerPolicy: 'visible_in_tracker',
    handoverPolicy: 'supporting_evidence',
    usesSharedLists: false,
    requiresSetupReady: true,
    offlineRouteId: 'reports',
  }),
  feature({
    routeId: 'handover',
    path: '/handover',
    label: 'Handover',
    pillar: 'delivery',
    domain: 'Delivery',
    subdomain: 'Handover & Closeout',
    layer: 'operation',
    trackerPolicy: 'creates_tracker_items',
    handoverPolicy: 'transfer_package',
    usesSharedLists: true,
    requiresSetupReady: true,
    offlineRouteId: 'handover',
  }),
] as const satisfies readonly BuildFeatureContract[];

export function getBuildFeatureContract(routeId: string): BuildFeatureContract | undefined {
  return NAVIOBUILD_FEATURE_CONTRACTS.find((featureContract) => featureContract.routeId === routeId);
}

export function getBuildFeatureForPath(pathname: string): BuildFeatureContract | undefined {
  const sorted = [...NAVIOBUILD_FEATURE_CONTRACTS].sort((a, b) => b.path.length - a.path.length);
  return sorted.find((featureContract) =>
    featureContract.path === '/'
      ? pathname === '/'
      : pathname === featureContract.path || pathname.startsWith(`${featureContract.path}/`)
  );
}

export function buildFeatureOfflineGap(routeId: string): string | null {
  const featureContract = getBuildFeatureContract(routeId);
  if (!featureContract) return `Missing feature contract for ${routeId}`;
  return getNavioBuildOfflineCapability(featureContract.offlineRouteId)
    ? null
    : `Missing offline capability for ${routeId}`;
}

export function buildLaunchFeatureRouteIds(): string[] {
  return NAVIOBUILD_OFFLINE_CAPABILITIES.filter((capability) => capability.requiredForLaunch).map(
    (capability) => capability.routeId
  );
}
